// @flow
import React from 'react';
import { animated } from '@react-spring/web';
import styled, { css, createGlobalStyle } from 'styled-components';
import { TabList } from '@reach/tabs';
import '@reach/tabs/styles.css';
import TypographyTheme from '../../typography/theme';
import { useSideScroll } from './use_side_scroll';
import ScrollControls from './scroll_controls';

type TabListProps = {
  children: React$Node,
};

export const StyledTabList = ({ children }: TabListProps) => {
  const {
    setContainerRef,
    canScrollLeft,
    canScrollRight,
    scrollProps,
    sideScroll,
  } = useSideScroll();

  return (
    <>
      <GlobalStyle />
      <StyledTabListWithControls>
        <StyledTabListAnimatedWrapper
          ref={setContainerRef}
          style={{
            position: 'relative',
            width: '100%',
            overflow: 'auto',
          }}
          scrollLeft={scrollProps.scrollLeft}
        >
          <StyledTabListWrapper>{children}</StyledTabListWrapper>
        </StyledTabListAnimatedWrapper>
        <ScrollControls
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
          sideScroll={sideScroll}
        />
      </StyledTabListWithControls>{' '}
    </>
  );
};

const StyledTabListWithControls = styled.div`
  position: relative;
`;

const hideScrollbar = css`
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  /* Chrome, Safari and Opera */
  &&::-webkit-scrollbar {
    display: none;
  }
`;

// This component is only necessary because of an incompatibility with styled-components v3.
// Currently, we cannot combine animated(styled(TabList)`...`), so we are splitting the
// functionality into two different components.
const StyledTabListAnimatedWrapper = React.forwardRef(
  ({ children, ...rest }, ref) => (
    <animated.div
      ref={ref}
      className="birch-tab-list-animated-wrapper"
      {...rest}
    >
      {children}
    </animated.div>
  )
);
StyledTabListAnimatedWrapper.displayName = 'StyledTabListAnimatedWrapper';

const GlobalStyle = createGlobalStyle`
  .birch-tab-list-animated-wrapper {
    ${hideScrollbar}
  }
`;

const StyledTabListWrapper = styled((props) => <TabList {...props} />)`
  && {
    background: none;
    column-gap: clamp(
      1.5rem,
      4rem,
      calc(100% / ${({ children }) => React.Children.count(children) * 2})
    );
  }

  > [data-reach-tab] {
    padding: 0.5rem 0;
    white-space: nowrap;

    &:active {
      background: none;
    }

    &:hover {
      border-bottom-color: ${TypographyTheme.colors.active};
    }

    &[data-selected] {
      color: ${TypographyTheme.colors.active};
    }
  }
`;
